import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'

const Settings = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Event Settings let organizers manage every aspect of their Event after it
            has been created. Only{' '}
            <Link href='/docs/guilds/members'>Members</Link> with the Organizer or
            Editor role can access Event Settings. You can reach them by clicking the{' '}
            <Paragraph size='$5' fontWeight='$7'>Settings</Paragraph> button on any
            Event page you manage.
          </Trans>
        </Paragraph>
        {/* TODO: Capture screenshot of Event Settings page showing the event-specific configuration sections */}
      </Card>

      <Card gap='$4'>
        <DocSection id='settings-sections' title={t`Settings Sections`}>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='settings' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>General</Paragraph> — Update the
                  Event title, description, start and end times, timezone, and cover photo.
                  Toggle whether the Event is{' '}
                  <Link href='/docs/events/visibility'>listed or unlisted</Link>.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='location' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Venue</Paragraph> — Toggle
                  in-person attendance on or off. When enabled, add a venue address that
                  attendees see as a clickable map link. Optionally set an attendee limit
                  to cap physical capacity — when the limit is reached, additional RSVPs
                  are placed on a waitlist.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='globe' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Virtual</Paragraph> — Toggle
                  online attendance on or off. When enabled, add a meeting link (Zoom,
                  Google Meet, or any URL) that attendees see after RSVPing. The link stays
                  private until someone confirms their attendance.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people-community' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Hosts</Paragraph> — Manage{' '}
                  <Link href='/docs/events/multi-host'>co-hosts</Link> for your Event.
                  Invite other Guilds or Users as co-hosts so they appear alongside the
                  primary host on the Event page.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Presentations</Paragraph> — Add{' '}
                  <Link href='/docs/presentations/overview'>Presentations</Link> to your
                  Event's schedule. Each Presentation can have its own speaker, title, and
                  description.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='ticket-diagonal' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Tickets</Paragraph> — Create and
                  manage{' '}
                  <Link href='/docs/events/tiers'>ticketing tiers</Link> for paid or free
                  registration. Set prices, quantities, sale windows, and configure{' '}
                  <Link href='/docs/events/discounts'>discount codes</Link>.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='edit' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Attendee Forms</Paragraph> — Add
                  custom{' '}
                  <Link href='/docs/events/attendee-forms'>registration forms</Link> to
                  collect information from attendees during RSVP or ticket purchase.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='mail' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Messages</Paragraph> — Send{' '}
                  <Link href='/docs/events/messages'>messages</Link> to all attendees.
                  Useful for sharing last-minute updates, venue changes, or post-Event
                  follow-ups.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>
    </>
  )
}

export default Settings
